// Shared read contract. Legacy normalization never upgrades a record's provenance.
const feeMath = require('./serviceFeePolicy');
const { USAGE_LABELS, usageMeta } = require('./usageContract');
const LABELS = Object.freeze({
    ...USAGE_LABELS,
    user_registered: 'User registered', username_set: 'Username changed',
    space_created: 'Space created', space_header_updated: 'Space header updated',
    space_published: 'Space published', space_deleted: 'Space deleted',
    component_created: 'Published component created', component_updated: 'Published component updated',
    component_deleted: 'Published component deleted', post_created: 'Post created',
    post_updated: 'Post updated', post_deleted: 'Post deleted',
    donation_created: 'Donation recorded', donation_confirmed: 'Donation confirmed',
    direct_payment_confirmed: 'Direct payment confirmed', mint_created: 'Mint recorded', mint_confirmed: 'Mint confirmed',
});
const text = (value, max = 128) => typeof value === 'string' ? value.slice(0, max) : '';
const atomic = value => typeof value === 'string' && /^\d{1,20}$/.test(value) ? value : '';
function timestampMs(value) {
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?.seconds === 'number') return value.seconds * 1000 + (value.nanoseconds || 0) / 1e6;
    if (typeof value?._seconds === 'number') return value._seconds * 1000 + (value._nanoseconds || 0) / 1e6;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
}
function isVerifiedPayment(record) {
    return record?.status === 'confirmed' && record?.verificationVersion === 1 &&
        record?.verificationOrigin === 'solana_rpc' && !!record.txSig &&
        ['devnet', 'mainnet-beta'].includes(record.cluster) && !!atomic(record.amountRaw);
}
function paymentCreditAtLeast(records, threshold) {
    if (!records.length) return false;
    const decimals = records[0].decimals;
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18) return false;
    try {
        const required = BigInt(feeMath.decimalToAtomic(threshold, decimals));
        if (required <= 0n || records.some(record => !isVerifiedPayment(record) || record.decimals !== decimals)) return false;
        return records.reduce((sum, record) => sum + BigInt(record.amountRaw), 0n) >= required;
    } catch { return false; }
}
function normalizeActivity(record = {}, id = '', { trusted = false } = {}) {
    const meta = record.meta || {};
    const type = Object.hasOwn(LABELS, record.type) ? record.type : 'legacy_activity';
    const canonical = trusted && record.schemaVersion === 1 && record.origin === 'backend';
    const financial = /^(donation|mint|direct_payment)_/.test(type);
    const candidateStatus = record.transactionStatus || meta.status ||
        (['confirmed', 'failed', 'pending', 'expired'].includes(record.status) ? record.status : 'unknown');
    const transactionStatus = financial && ['confirmed', 'failed', 'pending', 'expired'].includes(String(candidateStatus).toLowerCase())
        ? String(candidateStatus).toLowerCase() : financial ? 'unknown' : 'not_applicable';
    const actorUid = text(record.actorUid || meta.sender || meta.donorUid || meta.ownerUid);
    // A payout receiver is not necessarily the Space owner.
    const spaceOwnerUid = text(record.spaceOwnerUid || meta.spaceOwnerUid || meta.creatorUserId || meta.pageOwnerId || meta.space?.publicKey);
    const sourceId = text(record.source?.documentId || record.subjectId || record.objectId || meta.mintId || id);
    const collection = text(record.source?.collection || (type.startsWith('post_') ? 'posts' : type.startsWith('donation_') ? 'donations' : type.startsWith('mint_') ? 'mints' : type.startsWith('direct_payment_') ? 'directPayments' : 'users'));
    const decimals = Number.isInteger(meta.decimals) && meta.decimals >= 0 && meta.decimals <= 18 ? meta.decimals : 9;
    let amountRaw = atomic(meta.amountRaw || meta.priceAtomic);
    if (!amountRaw && typeof (meta.amount ?? meta.amountSol) === 'number') {
        try { amountRaw = feeMath.decimalToAtomic(meta.amount ?? meta.amountSol, decimals); } catch { /* display-only legacy amount */ }
    }
    const occurredAt = timestampMs(record.occurredAt || record.publishedAt || record.createdAt);
    const safeMeta = {
        ...(Object.hasOwn(USAGE_LABELS, type) ? usageMeta(meta, canonical) : {}),
        componentId: text(meta.componentId || meta.compId || meta.component?.id),
        componentType: text(meta.componentType || meta.component?.type, 32),
        gateType: text(meta.gateType || meta.component?.gate?.type, 32),
        space: { publicKey: spaceOwnerUid, muultiName: text(meta.space?.muultiName, 27) },
        txSig: text(meta.txSig || meta.signature), cluster: text(meta.cluster, 16),
        assetAddress: text(meta.assetAddress || meta.assetPubkey || meta.mint),
        collectionId: text(meta.collectionId), offerId: text(meta.offerId),
        amountRaw, decimals, currency: text(meta.currency || meta.paymentToken || 'SOL', 16),
        recipient: text(meta.recipient || meta.receiver || meta.payTo),
        creatorProceedsAtomic: atomic(meta.creatorProceedsAtomic),
    };
    return {
        id: text(id || record.eventId), eventId: text(record.eventId || id), schemaVersion: canonical ? 1 : 0,
        type, actorUid, actorName: text(record.actorName, 64), spaceOwnerUid,
        source: { collection, documentId: sourceId, path: text(record.source?.path || collection + '/' + sourceId, 300), version: text(record.source?.version) },
        subjectId: text(record.subjectId || record.objectId || sourceId), subjectUid: spaceOwnerUid || null,
        objectId: text(record.objectId || record.subjectId || sourceId),
        occurredAt, recordedAt: timestampMs(record.recordedAt || record.createdAt),
        createdAt: occurredAt, publishedAt: occurredAt,
        origin: canonical ? 'backend' : 'legacy', transactionStatus,
        deliveryStatus: canonical && ['not_requested', 'pending', 'delivered', 'no_recipients'].includes(record.deliveryStatus) ? record.deliveryStatus : 'not_requested',
        summary: (financial && transactionStatus === 'failed' ? (type.startsWith('donation_') ? 'Donation failed' : 'Transaction failed')
            : LABELS[type] || 'Historical activity') + (!canonical && financial ? ' (unverified history)' : ''),
        meta: safeMeta,
    };
}
module.exports = { LABELS, timestampMs, isVerifiedPayment, paymentCreditAtLeast, normalizeActivity, formatUnits: feeMath.formatUnits };
